import React, { useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-hot-toast";
import { CardComp, ButtonComp, TableCompData, TextInputComp } from "../../../components";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { convertToTitleCase } from "../../../utils/helpers";
import {
  useGetMemberReferralSettingsQuery,
  useUpdateMemberReferralSettingsMutation,
  useGetStaffReferralInvitesQuery,
} from "../../../store/admin/settings/memberReferralStaffSlice";

const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "sent") return "#19BC31";
  if (s === "failed") return "#FF4343";
  if (s === "recorded") return "#31778C";
  return "#A1A0A0";
};

/**
 * Branch-scoped member referral invite toggle + invite history for active branch.
 * Embedded on Support Settings (no new module catalog slug).
 */
export default function MemberReferralStaffSection() {
  const { data: settings, isLoading: settingsLoading } =
    useGetMemberReferralSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateMemberReferralSettingsMutation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const {
    data: invitesPayload,
    isLoading: invitesLoading,
    isFetching: invitesFetching,
  } = useGetStaffReferralInvitesQuery({ page, limit: 10, search });

  const invites = invitesPayload?.data || [];
  const totalPages = invitesPayload?.last_page || 1;

  const handleToggle = async (checked) => {
    try {
      const response = await updateSettings({ is_enabled: checked }).unwrap();
      if (response?.status === true) {
        toast.success(
          checked
            ? "Member referral invites enabled for this branch"
            : "Member referral invites disabled for this branch"
        );
      } else {
        toast.error(response?.message || "Failed to update setting");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Could not update referral setting");
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Date",
        selector: (row) =>
          row?.created_at
            ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
            : "—",
        minWidth: "140px",
      },
      {
        name: "Referrer",
        selector: (row) => {
          const name = row?.referrer?.name || "Member";
          const no = row?.referrer?.member_no;
          return no ? `${name} · ${no}` : name;
        },
        minWidth: "180px",
        wrap: true,
      },
      {
        name: "Channel",
        cell: (row) => (
          <span className="small">{convertToTitleCase(row?.channel || "—")}</span>
        ),
        width: "100px",
      },
      {
        name: "Contact",
        selector: (row) => row?.friend_email || row?.friend_phone || "—",
        minWidth: "160px",
        wrap: true,
      },
      {
        name: "Status",
        cell: (row) => (
          <span
            className="badge rounded-pill text-white px-3 py-2"
            style={{
              backgroundColor: statusColor(row?.status),
              minWidth: "80px",
            }}
          >
            {convertToTitleCase(row?.status || "unknown")}
          </span>
        ),
        width: "120px",
      },
      {
        name: "Notes",
        selector: (row) => row?.failure_reason || "—",
        minWidth: "180px",
        wrap: true,
      },
    ],
    []
  );

  if (settingsLoading) {
    return (
      <CardComp cardClassName="rounded-4 shadow3 border-0 mt-4">
        <TableSkeletonLoader rows={4} columns={1} />
      </CardComp>
    );
  }

  const isEnabled = settings?.is_enabled ?? true;

  return (
    <div className="mt-4 space-y-4">
      <CardComp cardClassName="rounded-4 shadow3 border-0">
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div>
            <h5 className="fw-bold text-black2 mb-1">Member referral invites</h5>
            <p className="text-blacklight mb-0 small">
              When enabled, members in{" "}
              <strong>{settings?.branch_name || "this branch"}</strong> can
              share referral links and invite friends by email (free) or SMS
              (charged to the branch SMS wallet). When disabled, the feature is
              hidden on the member portal and mobile app.
            </p>
          </div>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="member_referral_invites_enabled"
              checked={isEnabled}
              disabled={isUpdating}
              onChange={(e) => handleToggle(e.target.checked)}
              style={{
                width: "3em",
                height: "1.5em",
                cursor: isUpdating ? "wait" : "pointer",
              }}
              aria-label="Enable member referral invites"
            />
          </div>
        </div>
      </CardComp>

      <CardComp cardClassName="rounded-4 shadow3 border-0">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
          <div>
            <h5 className="fw-bold text-black2 mb-1">Referral invite history</h5>
            <p className="text-blacklight mb-0 small">
              Invites sent by members in the active branch (visible even when
              the feature is disabled).
            </p>
          </div>
          <form
            className="d-flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearch(searchInput.trim());
            }}
          >
            <TextInputComp
              name="referral_invite_search"
              placeholder="Search referrer, email, phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              LabelText=""
            />
            <ButtonComp
              type="submit"
              btnText="Search"
              btnClassName="border-0 text-white rounded py-2 px-3"
              ButtonInlineStyle={{
                backgroundColor: "#31778c",
                minWidth: "fit-content",
                whiteSpace: "nowrap",
              }}
            />
          </form>
        </div>

        <TableCompData
          columns={columns}
          data={invites}
          progressPending={invitesLoading || invitesFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={6} />}
          pagination={false}
          noDataComponent={
            <div className="py-4 text-muted small">
              No referral invites yet for this branch.
            </div>
          }
        />

        <div className="d-flex align-items-center justify-content-between mt-3">
          <p className="small text-muted mb-0">
            {invitesPayload?.total
              ? `${invitesPayload.total} invite${
                  invitesPayload.total === 1 ? "" : "s"
                }`
              : null}
          </p>
          <div className="d-flex gap-2">
            <ButtonComp
              type="button"
              btnText="Previous"
              disabled={page <= 1 || invitesFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              btnClassName="border rounded py-2 px-3"
              ButtonInlineStyle={{ minWidth: "fit-content" }}
            />
            <ButtonComp
              type="button"
              btnText="Next"
              disabled={page >= totalPages || invitesFetching}
              onClick={() => setPage((p) => p + 1)}
              btnClassName="border-0 text-white rounded py-2 px-3"
              ButtonInlineStyle={{
                backgroundColor: "#31778c",
                minWidth: "fit-content",
              }}
            />
          </div>
        </div>
      </CardComp>
    </div>
  );
}
